import { getCookies, setClientCookie } from './cookies.js';
import logger from './logger.js';
import { N as NEVER_RESTORE_COOKIES } from '../cookies-l-jEMp6P.js';
import 'cookies-next';
import '../currency-_kacFJHO.js';
import '../language-BHHwgtQp.js';
import '../payment-YC9H491Y.js';
import './isBot.js';
import './logToSlack.js';

/**
 * ## CookieRestorer
 *
 * Reads the current cookies and saves them to local storage.
 * If cookie gets deleted, it creates it again based on this
 *
 * @param options
 */
const CookieRestorer = ({ options } = {}) => {
    const time0 = (options === null || options === void 0 ? void 0 : options.verbose) ? Date.now() : 0;
    const storageKey = (options === null || options === void 0 ? void 0 : options.storageKey) || 'cookieRes';
    const cookiesToBrowser = [];
    const cookiesToStorage = [];
    // Validate if local storage exists
    if (typeof localStorage === 'undefined') {
        if (options === null || options === void 0 ? void 0 : options.verbose)
            logger('CookieRestorer\t->\tlocalStorage not found', { type: 'error' });
        return;
    }
    // Get current cookies from browser
    const browserCookies = Object.entries(getCookies()).map(([key, val]) => ({
        key,
        val,
    }));
    // Get stored cookies from local storage
    const storedCookies = JSON.parse(localStorage.getItem(storageKey) || '[]');
    // Check missing cookies in the browser
    storedCookies.forEach(storedCookie => {
        // Skip cookies that should never be restored
        if (NEVER_RESTORE_COOKIES.includes(storedCookie.key))
            return;
        const currentCookieIndex = browserCookies.findIndex(currentCookie => currentCookie.key === storedCookie.key);
        if (currentCookieIndex === -1) {
            cookiesToBrowser.push({ key: storedCookie.key, val: storedCookie.val });
        }
    });
    // Check missing cookies in local storage. If cookie has new content, add the cookie again
    browserCookies.forEach(currentCookie => {
        // Skip cookies that should never be restored
        if (NEVER_RESTORE_COOKIES.includes(currentCookie.key))
            return;
        const storedCookieIndex = storedCookies.findIndex(storedCookie => storedCookie.key === currentCookie.key);
        if (storedCookieIndex === -1) {
            cookiesToStorage.push({ key: currentCookie.key, val: currentCookie.val });
        }
        else if (storedCookies[storedCookieIndex].val !== currentCookie.val) {
            storedCookies.splice(storedCookieIndex, 1);
            cookiesToStorage.push({ key: currentCookie.key, val: currentCookie.val });
        }
    });
    // Update local storage with new cookies
    if (cookiesToStorage.length > 0) {
        const updatedStoredCookies = [...storedCookies, ...cookiesToStorage];
        localStorage.setItem(storageKey, JSON.stringify(updatedStoredCookies));
        if (options === null || options === void 0 ? void 0 : options.verbose)
            logger(`CookieRestorer\t->\tCookies saved to local storage: ${cookiesToStorage.length}`, {
                type: 'log',
            });
    }
    // Update document cookie with restored cookies
    if (cookiesToBrowser.length > 0) {
        const date = new Date();
        date.setTime(date.getTime() + 300 * 24 * 60 * 60 * 1000);
        cookiesToBrowser.forEach(cookie => {
            // Using "setClientCookie" method because need to avoid the stringify method call
            // for the "CookieConsent" cookie value
            // because we don't want to encode some special characters from this value, e.g. "{stamp:%27"
            setClientCookie(cookie.key, cookie.val, date, '/');
        });
        if (options === null || options === void 0 ? void 0 : options.verbose)
            logger(`CookieRestorer\t->\tCookies updated in browser: ${cookiesToBrowser.length}`, {
                type: 'log',
            });
    }
    const time1 = (options === null || options === void 0 ? void 0 : options.verbose) ? Date.now() : 0;
    if (options === null || options === void 0 ? void 0 : options.verbose)
        logger(`CookieRestorer\t->\tCookie Restorer took ${time1 - time0}ms`, { type: 'log' });
};
/**
 * ## Restorer Intervals
 *
 * Restores cookies every 2000ms for 60s by default
 *
 * @param options
 */
const restorerIntervals = ({ options } = {}) => {
    const startTime = Date.now();
    const totalTimeout = (options === null || options === void 0 ? void 0 : options.totalTimeout) || 60000;
    const intervalTime = (options === null || options === void 0 ? void 0 : options.interval) || 2000;
    let restorerInterval = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        if (options === null || options === void 0 ? void 0 : options.verbose)
            logger(`CookieRestorer\t->\tTime Elapsed -> ${elapsedTime / 1000}s`, { type: 'log' });
        if (elapsedTime < totalTimeout) {
            CookieRestorer({ options });
        }
        else {
            clearInterval(restorerInterval);
            restorerInterval = null;
        }
    }, intervalTime);
    setTimeout(() => {
        if (restorerInterval) {
            clearInterval(restorerInterval);
            restorerInterval = null;
        }
    }, totalTimeout);
};

export { CookieRestorer, restorerIntervals };
//# sourceMappingURL=cookieRestorer.js.map
